/**
 * Sharing (spec docs/superpowers/specs/2026-09-25-file-access-and-sharing-design.md,
 * Train B). General access is Restricted or Anyone with the link; people are Vestra
 * users added by their exact email, each Can view. The gate (public.file_access)
 * decides every read; these are the shapes the share routes and the web agree on.
 */
export type ShareResourceType = 'file' | 'creation';
/** `restricted` is file_metadata.visibility 'private' / task_artifacts.shared false. */
export type GeneralAccess = 'restricted' | 'link';

export interface SharePerson {
  userId: string;
  name: string;
  email: string;
  grantedAt: string;
}

export interface ShareState {
  type: ShareResourceType;
  /** The id writes go to: the file id, or the creation lineage head's public id. */
  id: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
  general: GeneralAccess;
  owner: { name: string; email: string };
  people: SharePerson[];
}

export type AddPersonResult =
  /** `notified`: whether the notice reached them in the app or by email (best effort; false when both legs failed). */
  | { status: 'added'; person: SharePerson; notified: boolean }
  | { status: 'already'; person: SharePerson }
  | { status: 'no_account' }
  | { status: 'self' };

export interface SharedWithMeItem {
  type: ShareResourceType;
  id: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
  sharedBy: { name: string };
  sharedAt: string;
}

/** Someone this person has shared with before: the only suggestions there are. */
export interface SuggestedPerson {
  name: string;
  email: string;
}

/** New-email lookups a person may make in an hour (spec: "Email lookup and privacy"). */
export const SHARE_LOOKUPS_PER_HOUR = 30;

const EMAIL = /^[^@\s,;<>"]+@[^@\s,;<>"]+\.[^@\s,;<>"]+$/;
const EMAIL_MAX = 254;

/** The one spelling an address is looked up by: trimmed, lowercased, shaped like an address; else null. */
export function normalizeShareEmail(raw: string): string | null {
  const v = raw.trim().toLowerCase();
  return v.length > 0 && v.length <= EMAIL_MAX && EMAIL.test(v) ? v : null;
}

/** A pasted run of addresses (commas, semicolons, spaces or new lines), each as typed. */
export function splitEmails(raw: string): string[] {
  return raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
}
